import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { costoDeUnidadVenta, formatMoney, formatQuantity, parseMoney, sumarDias, type Centavos, type Milli } from '@abastece/shared';
import type { Existencia, Producto, Venta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import type { DineroDeAnalisis, Tramo } from '../lib/analisis.js';
import { listaPredeterminada, precioDe, unidadesDe } from '../lib/catalogo.js';
import { costoConocido } from '../lib/costos.js';
import {
  DIAS_DE_RITMO,
  aPrecio,
  porCategoria,
  productosDeLaCategoria,
  simularPrecio,
  vendidasDe,
  type Agotado,
  type Sobrante,
} from '../lib/estante.js';
import { useT } from '../i18n/index.js';
import { etiquetaCategoriaProducto, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Campo } from './Campo.js';
import { ElegirEscribiendo } from './ElegirEscribiendo.js';
import { Barras } from './Graficas.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { AProducto, Corta, DetalleDeCifra, Fila, Nada, Seccion, pct, useFechas } from './PiezasDeAnalisis.js';

/**
 * What «Productos» in «Análisis» says besides what leaves the most and what stands still (the owner,
 * 2026-10-13: «let's build all of it»): each category against what it holds on the shelf, what has
 * run out and what each day without it goes unsold, what will take more than three months to sell,
 * and a price tried before it is set. The arithmetic is `lib/estante.ts`.
 */

/** The days a price is tried against: what sold the last month. */
const DIAS_SIMULADOS = 30;

/**
 * «Por categoría»: what each category sold and left in the period, against what its products hold
 * on the shelf today, and what that leaves a month. A category opens its products.
 */
export function PorCategoria({
  ventas,
  productos,
  existencias,
  tramo,
  dinero,
}: {
  ventas: readonly Venta[];
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  tramo: Tramo;
  dinero: DineroDeAnalisis;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const [abierta, setAbierta] = useState<string | null>(null);
  const filas = useMemo(() => porCategoria({ ventas, productos, existencias, tramo }), [ventas, productos, existencias, tramo]);
  const porId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);
  const detalle = (f: { ingreso: number; margenBp: number | null; estante: number; rindeBp?: number | null }) =>
    [
      f.ingreso > 0 ? t('analisis.categoria.vendio', { monto: dinero(f.ingreso), pct: pct(f.margenBp) }) : t('analisis.categoria.nadaVendido'),
      f.estante > 0 ? t('analisis.categoria.estante', { monto: dinero(f.estante) }) : null,
      f.rindeBp != null ? t('analisis.categoria.rinde', { pct: pct(f.rindeBp) }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  return (
    <Seccion titulo={t('analisis.categoria.titulo')} ayuda={t('analisis.categoria.ayuda')} datos="categorias">
      {filas.length === 0 ? (
        <Nada texto={t('analisis.sinVentas')} />
      ) : (
        <Barras
          filas={filas.map((f) => ({
            clave: f.categoria,
            etiqueta: etiquetaCategoriaProducto(f.categoria),
            valor: f.utilidad,
            texto: dinero(f.utilidad),
            detalle: detalle(f),
            onAbrir: () => setAbierta(f.categoria),
          }))}
        />
      )}
      {abierta !== null && (
        <DetalleDeCifra
          titulo={etiquetaCategoriaProducto(abierta)}
          datos={`categoria-${abierta}`}
          vacio={t('analisis.sinVentas')}
          onCerrar={() => setAbierta(null)}
          renglones={productosDeLaCategoria({ categoria: abierta, ventas, productos, existencias, tramo }).map((x) => {
            const p = porId.get(x.productoId);
            return {
              clave: x.productoId,
              titulo: <AProducto id={x.productoId} nombre={p ? nombre(p) : x.productoId} />,
              detalle: detalle(x),
              valor: dinero(x.utilidad),
              rojo: x.utilidad < 0,
            };
          })}
        />
      )}
    </Seccion>
  );
}

/**
 * «Agotados»: what the shop has none of and was selling, since when, and what each day without it
 * goes unsold at what it sold the four weeks before. «Por comprar» is where it gets ordered.
 */
export function Agotados({ lista, productos, dinero }: { lista: readonly Agotado[]; productos: ReadonlyMap<string, Producto>; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const nombre = useNombreProducto();
  const venta = lista.reduce((a, x) => a + x.ventaAlDia, 0);
  const utilidad = lista.reduce((a, x) => a + x.utilidadAlDia, 0);
  return (
    <Seccion
      titulo={t('analisis.agotado.titulo')}
      ayuda={t('analisis.agotado.ayuda')}
      datos="agotados"
      accion={
        lista.length > 0 ? (
          <Link to="/reponer" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" data-agotados-reponer>
            <Ico icono="reponer" className="size-4" />
            {t('reponer.titulo')}
          </Link>
        ) : undefined
      }
    >
      {lista.length === 0 ? (
        <Nada texto={t('analisis.agotado.nada')} />
      ) : (
        <>
          <p className="mb-2 text-sm text-slate-300" data-agotados-total>
            {t('analisis.agotado.total', { venta: dinero(venta), utilidad: dinero(utilidad) })}
          </p>
          <Corta filas={lista}>
            {(vistas) => (
              <ul className="divide-y divide-slate-800">
                {vistas.map((x) => {
                  const p = productos.get(x.productoId);
                  return (
                    <Fila
                      key={x.productoId}
                      nombre={x.productoId}
                      a={<AProducto id={x.productoId} nombre={p ? nombre(p) : x.nombre} />}
                      detalle={x.dias === 0 ? t('analisis.agotado.hoy') : t('analisis.agotado.desde', { fecha: f.corta(x.desde), monto: dinero(x.perdido) })}
                      valor={t('analisis.agotado.alDia', { monto: dinero(x.ventaAlDia) })}
                    />
                  );
                })}
              </ul>
            )}
          </Corta>
        </>
      )}
    </Seccion>
  );
}

/** A stretch of days as an owner says it: in months up to two years, in years from there. */
function useDuracion() {
  const t = useT();
  return (dias: number) => (dias < 730 ? t('analisis.sobra.meses', { n: Math.round(dias / 30) }) : t('analisis.sobra.anios', { n: Math.round(dias / 365) }));
}

/** «Mucho en el estante»: what sells, but holds more than three months of it. */
export function MuchoEnElEstante({ lista, productos, dinero }: { lista: readonly Sobrante[]; productos: ReadonlyMap<string, Producto>; dinero: DineroDeAnalisis }) {
  const t = useT();
  const nombre = useNombreProducto();
  const duracion = useDuracion();
  const total = lista.reduce((a, x) => a + x.valor, 0);
  return (
    <Seccion titulo={t('analisis.sobra.titulo')} ayuda={t('analisis.sobra.ayuda', { n: DIAS_DE_RITMO })} datos="sobra">
      {lista.length === 0 ? (
        <Nada texto={t('analisis.sobra.nada')} />
      ) : (
        <>
          <p className="mb-2 text-sm text-slate-300" data-sobra-total>
            {t('analisis.sobra.total', { monto: dinero(total), n: lista.length })}
          </p>
          <Corta filas={lista}>
            {(vistas) => (
              <ul className="divide-y divide-slate-800">
                {vistas.map((x) => {
                  const p = productos.get(x.productoId);
                  return (
                    <Fila
                      key={x.productoId}
                      nombre={x.productoId}
                      a={<AProducto id={x.productoId} nombre={p ? nombre(p) : x.nombre} />}
                      detalle={t('analisis.sobra.alcanza', { cantidad: `${formatQuantity(x.cantidad)} ${etiquetaUnidad(x.unidad, x.unidad)}`, tiempo: duracion(x.dias) })}
                      valor={dinero(x.valor)}
                    />
                  );
                })}
              </ul>
            )}
          </Corta>
        </>
      )}
    </Seccion>
  );
}

/** «Simular un precio»: a price tried before it is set, in a sheet. */
export function SimularUnPrecio({ ventas, hoyEs }: { ventas: readonly Venta[]; hoyEs: string }) {
  const t = useT();
  const [abierta, setAbierta] = useState(false);
  return (
    <Seccion
      titulo={t('analisis.simular.titulo')}
      ayuda={t('analisis.simular.ayuda', { n: DIAS_SIMULADOS })}
      datos="simular"
      accion={
        <button type="button" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" onClick={() => setAbierta(true)} data-simular-abrir>
          <Ico icono="calcular" className="size-4" />
          {t('analisis.simular.boton')}
        </button>
      }
    >
      <p className="text-sm text-slate-400">{t('analisis.simular.explica')}</p>
      {abierta && <SimularPrecio ventas={ventas} hoyEs={hoyEs} onCerrar={() => setAbierta(false)} />}
    </Seccion>
  );
}

function SimularPrecio({ ventas, hoyEs, onCerrar }: { ventas: readonly Venta[]; hoyEs: string; onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  const datos = useLiveQuery(async () => {
    const [productos, unidades, precios, listas, existencias, compras, config] = await Promise.all([
      db.productos.toArray(),
      db.unidades.toArray(),
      db.precios.toArray(),
      db.listas.toArray(),
      db.existencias.toArray(),
      db.compras.toArray(),
      readSettings(),
    ]);
    return { productos, unidades, precios, lista: listaPredeterminada(listas.filter((l) => l.activa)), existencias, compras, minimoBp: config.precioMargenMinimoBp };
  }, []);
  const [productoId, setProductoId] = useState('');
  const [unidadId, setUnidadId] = useState('');
  const [texto, setTexto] = useState('');

  // The products with a price to try, each presentation priced on the shop's own list.
  const conPrecio = useMemo(() => {
    if (!datos?.lista) return new Map<string, { id: string; nombre: string; factor: Milli; precio: Centavos }[]>();
    const m = new Map<string, { id: string; nombre: string; factor: Milli; precio: Centavos }[]>();
    for (const p of datos.productos) {
      if (!p.activo) continue;
      const us = unidadesDe(datos.unidades, p.id).flatMap((u) => {
        const precio = precioDe(datos.precios, u.id, datos.lista!.id);
        return precio !== null && precio > 0 ? [{ id: u.id, nombre: u.nombre, factor: u.factor, precio }] : [];
      });
      if (us.length > 0) m.set(p.id, us);
    }
    return m;
  }, [datos]);
  const opciones = useMemo(
    () => (datos ? datos.productos.filter((p) => conPrecio.has(p.id)).map((p) => ({ id: p.id, nombre: nombre(p) })) : []),
    [datos, conPrecio, nombre],
  );

  const unidades = conPrecio.get(productoId) ?? [];
  const unidad = unidades.find((u) => u.id === unidadId) ?? unidades[0];
  const costo = datos && unidad ? costoDeUnidadVenta(costoConocido(productoId, 'tienda', datos.existencias, datos.compras), unidad.factor) : null;
  const vendidas = unidad ? vendidasDe(ventas, productoId, unidad.factor, sumarDias(hoyEs, -(DIAS_SIMULADOS - 1)), hoyEs) : 0;
  const nuevo = texto.trim() === '' ? null : parseMoney(texto);
  const mal = texto.trim() !== '' && (nuevo === null || nuevo <= 0);
  const sim = unidad && costo !== null && nuevo !== null && nuevo > 0 ? simularPrecio({ precio: unidad.precio, costo, nuevo, vendidas }) : null;
  const hoy = unidad && costo !== null ? aPrecio(unidad.precio, costo) : null;
  // A count alone: the presentation is said above it, and a name like «Bolsa 5 lb» has no plural.
  const cuantas = (milli: number) => formatQuantity(milli as Milli);

  return (
    <Modal
      titulo={t('analisis.simular.titulo')}
      onCerrar={onCerrar}
      pie={
        productoId ? (
          <Link to="/productos/$productoId" params={{ productoId }} className="btn-secundario w-full" data-simular-producto>
            <Ico icono="precios" />
            {t('analisis.simular.alProducto')}
          </Link>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4" data-simular>
        <ElegirEscribiendo
          etiqueta={t('analisis.simular.producto')}
          placeholder={t('analisis.simular.buscar')}
          opciones={opciones}
          value={productoId}
          onChange={(id) => {
            setProductoId(id);
            setUnidadId('');
            setTexto('');
          }}
          datos="simular-producto"
        />
        {unidad && hoy && costo !== null && (
          <>
            {unidades.length > 1 && (
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('linea.presentacion')} data-simular-unidades>
                {unidades.map((u) => {
                  const esta = u.id === unidad.id;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      role="radio"
                      aria-checked={esta}
                      data-sin-icono="pestana"
                      className={`min-h-10 rounded-lg px-3 text-sm transition ${esta ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                      onClick={() => {
                        setUnidadId(u.id);
                        setTexto('');
                      }}
                      data-simular-unidad={u.id}
                    >
                      {etiquetaUnidad(u.nombre, u.nombre)}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300" data-simular-hoy>
              <p>
                {t('analisis.simular.hoy', {
                  unidad: etiquetaUnidad(unidad.nombre, unidad.nombre),
                  precio: formatMoney(hoy.precio),
                  costo: formatMoney(costo),
                  deja: formatMoney(hoy.deja),
                  pct: pct(hoy.margenBp),
                })}
              </p>
              <p className="text-slate-400" data-simular-vendidas>
                {vendidas > 0
                  ? t('analisis.simular.vendidas', { n: DIAS_SIMULADOS, cuantas: cuantas(vendidas), monto: formatMoney(Math.round((vendidas * hoy.deja) / 1_000) as Centavos) })
                  : t('analisis.simular.sinVentas', { n: DIAS_SIMULADOS })}
              </p>
            </div>
            <Campo
              etiqueta={t('analisis.simular.nuevo')}
              inputMode="decimal"
              autoComplete="off"
              placeholder={(unidad.precio / 100).toFixed(2)}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              error={mal ? t('analisis.simular.mal') : null}
              data-simular-precio
            />
            {sim && (
              <div className="flex flex-col gap-1 text-sm" data-simulacion>
                <p className="font-medium" data-simular-deja>
                  {t('analisis.simular.deja', { precio: formatMoney(sim.nuevo.precio), deja: formatMoney(sim.nuevo.deja), pct: pct(sim.nuevo.margenBp) })}
                </p>
                {sim.nuevo.deja <= 0 ? (
                  <p className="text-rose-400" data-simular-aviso="perdida">
                    {t('analisis.simular.perdida')}
                  </p>
                ) : (
                  datos &&
                  sim.nuevo.margenBp < datos.minimoBp && (
                    <p className="text-amber-300" data-simular-aviso="minimo">
                      {t('analisis.simular.bajoMinimo', { pct: pct(datos.minimoBp) })}
                    </p>
                  )
                )}
                {sim.mismas !== null && sim.nuevo.precio !== sim.antes.precio && (
                  <p className="text-slate-300" data-simular-mismas>
                    {t(sim.nuevo.precio > sim.antes.precio ? 'analisis.simular.menos' : 'analisis.simular.mas', {
                      mismas: cuantas(sim.mismas),
                      vendidas: cuantas(sim.vendidas),
                      n: DIAS_SIMULADOS,
                    })}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
